import type { ReactNode } from "react";
import type { BuyerLeadDetailDto } from "@/api/buyers";
import {
  StatCard,
  StatsBar,
  StatusBadge,
  Timestamp,
  Tooltip,
} from "@/design-system/components/ui";
import { buyerLabel } from "./buyerFilters";
import { submissionStatusTone } from "./buyerListBits";

/**
 * The six facts a specialist opening a buyer cold needs before anything else.
 *
 * <p><b>Every tile explains itself.</b> All six labels are terms of art, and two
 * of them are numbers somebody is expected to act on, so each carries a
 * {@link Tooltip} defining what it counts — on hover AND on keyboard focus, with
 * the same words rendered <c>sr-only</c> so touch and screen-reader users get
 * them from the label's own text rather than from a hover they do not have.</p>
 *
 * <p>🔴 <b>The defect this exists to fix.</b> Overview was eleven stacked cards
 * with no summary and no rail, so the first ~20 rows a reader met were
 * label/value pairs — the page's centre of gravity was data entry, not
 * orientation. Every status signal was below the fold: prequalification was row
 * 3 of a milestone ladder near the bottom, and the two ageing stamps
 * (`assignedAtUtc`, `lastContactAtUtc`) were on the DTO and rendered
 * <em>nowhere in the programme at all</em>.</p>
 *
 * <p>Everything here is already on `BuyerLeadDetailDto`, or comes off the ONE
 * submissions read Overview already makes. The strip itself holds no query — the
 * two facts that need one are handed in as resolved states — because a summary
 * that fetches is a summary that can be half-loaded, and half-loaded orientation
 * is worse than none.</p>
 *
 * <p><b>Weight is deliberate.</b> Days-since-assignment is the ageing signal and
 * the only bare numeral, so it keeps `StatCard`'s native display size; the dates
 * and badges render a step down. A relative `Timestamp` was the obvious
 * alternative for the ageing card — the assignment queue's "Waiting since"
 * column uses exactly that — but `formatRelativeTime` falls back to an absolute
 * date past 30 days, which drops the count precisely on the stale buyers the
 * signal exists to catch. The two genuinely relative facts (last contact, next
 * follow-up) do use `Timestamp`, which is what it is for.</p>
 *
 * <p><b>Status is not repeated.</b> The header already carries the funnel status
 * and the control that changes it, so the strip's status slot shows what the
 * header cannot: how far this buyer has actually got with any listing.</p>
 *
 * <p><b>NDA-on-file has landed, and it cost the server change it was waiting
 * for.</b> It was left out because the NDA fields lived only on
 * `BuyerSubmissionDetailDto`, so reading it meant a fetch per submission — an
 * N+1 behind a summary strip. `BuyerSubmissionSummaryDto` now carries a
 * server-computed `ndaOnFile`, so the whole count comes off the ONE list read
 * the Submissions tab already makes.</p>
 *
 * <p>🔴 <b>That does mean Overview now makes a read the strip's opening claim
 * says it does not.</b> The claim is corrected rather than quietly broken: the
 * strip itself still holds no query — it is handed a resolved
 * {@link BuyerNdaChip} — and the read is the SAME query key the Submissions tab
 * uses, so React Query serves both from one request. The half-loaded objection
 * is answered by saying so: loading and unreadable are their own states, and
 * neither renders as "none on file".</p>
 */
export function BuyerSummaryStrip({
  buyer: b,
  nda,
  followUp,
}: {
  buyer: BuyerLeadDetailDto;
  /** Required, not optional: a caller that forgets it would silently render a
   *  strip missing a fact, and a typecheck failure is the cheaper way to find
   *  that out. */
  nda: BuyerNdaChip;
  /** Same contract as {@link BuyerNdaChip} — a resolved state, never a query. */
  followUp: BuyerFollowUpChip;
}) {
  const held = daysSince(b.assignedAtUtc);

  return (
    <StatsBar columns={6} data-testid="buyer-summary">
      {/*
        The header's status badge answers "where is this buyer in our funnel".
        This answers the different question "how far have they actually got with
        a listing", which nothing above the fold said. An absent furthest status
        with submissions on file is "we do not know", NOT "not presented" —
        the same tri-state discipline the SBA field and the Unknown verdict use.
      */}
      <StatCard
        label={
          <Hint text="The furthest any of this buyer's submissions has got. The header's badge is a different question — where the BUYER is in our funnel.">
            Furthest submission
          </Hint>
        }
        data-testid="buyer-summary-pipeline"
        value={
          b.furthestSubmissionStatus ? (
            <Pill>
              <StatusBadge tone={submissionStatusTone(b.furthestSubmissionStatus)}>
                {buyerLabel(b.furthestSubmissionStatus)}
              </StatusBadge>
            </Pill>
          ) : (
            <Quiet>{b.submissionCount > 0 ? "—" : "Not presented"}</Quiet>
          )
        }
      />

      {/*
        Unassigned and "assigned, but the stamp predates the column" are
        different facts and are said differently. Neither is toned: an
        unassigned buyer is an ordinary state of the queue, not a fault.
      */}
      <StatCard
        label={
          <Hint text="Whole days since this buyer was assigned to their consultant. The ageing signal — a high number on an un-worked record is the thing to catch.">
            Days since assignment
          </Hint>
        }
        data-testid="buyer-summary-assigned"
        value={
          held != null ? (
            held
          ) : (
            <Quiet>{b.assignedSpecialistName ? "—" : "Unassigned"}</Quiet>
          )
        }
      />

      {/* No stamp means nothing is on record — which is not the same claim as
          "nobody has ever spoken to them", so the copy says the former. */}
      <StatCard
        label={
          <Hint text="The last contact recorded against this buyer. Blank means nothing is on record — not that nobody has spoken to them.">
            Last contact
          </Hint>
        }
        data-testid="buyer-summary-contact"
        value={
          b.lastContactAtUtc ? (
            <Timestamp value={b.lastContactAtUtc} className="text-body text-onyx" />
          ) : (
            <Quiet>None recorded</Quiet>
          )
        }
      />

      {/*
        🔴 The ROLL-UP, at last, and it cost no server change.

        This card used to render the buyer-level `nextFollowUpDate` alone, with a
        label — "Follow-up (this record)" — that existed to disclaim what the
        card was not: the soonest date across everything this buyer is in play
        on. The disclaimer was honest, but it made the one card a specialist most
        wants to act on the one card they cannot act on, because the date that
        matters is whichever comes first and it is usually a submission's.

        The stated reason for the omission — that reaching the submissions would
        cost a second request on mount — has since stopped being true. Overview
        already reads the submissions list for the NDA count, on the SAME query
        key the Submissions tab uses, so both are served from one request; and
        `nextFollowUpDate` is on every row of it. The roll-up is a `Math.min`
        over data already in hand.
      */}
      <StatCard
        label={
          <Hint text="The soonest follow-up due across this buyer's own record and every submission they are in play on — whichever comes first.">
            Next follow-up
          </Hint>
        }
        data-testid="buyer-summary-followup"
        value={<FollowUpValue followUp={followUp} />}
      />

      {/*
        Promoted out of the milestone ladder, where it was row 3 of 7 near the
        bottom of the page. It stays a rung down there as well — the ladder lists
        every milestone reached or not, which is the question it exists to
        answer, and pulling one out would leave a gap rather than a summary.

        Not-completed is `neutral`, never `warn`: an incomplete prequalification
        is the ordinary state of a new buyer, and the module's own rule is that
        an unassessed thing must not read as either cleared or failed.
      */}
      <StatCard
        label={
          <Hint text="Whether this buyer has completed the prequalification funnel. Not completed is the ordinary state of a new buyer — it is neither a pass nor a failure.">
            Prequalification
          </Hint>
        }
        data-testid="buyer-summary-prequal"
        value={
          <Pill>
            {b.prequalCompletedAtUtc ? (
              <StatusBadge tone="ok">Completed</StatusBadge>
            ) : (
              <StatusBadge tone="neutral">Not completed</StatusBadge>
            )}
          </Pill>
        }
      />

      {/*
        The NDA question, across everything this buyer has been presented to.

        A count rather than a badge, because one bool per submission cannot say
        WHICH way each was settled and a badge would have to pick a word for
        both. "2 of 3" claims only what it can support; the submission's own
        surface says whether it was signed or recorded as not required.

        Counted over the same page of rows the Submissions tab renders, so the
        strip and the table below it can never disagree.
      */}
      <StatCard
        label={
          <Hint text="How many of this buyer's submissions have the NDA question settled — signed, or explicitly recorded as not required.">
            NDA on file
          </Hint>
        }
        data-testid="buyer-summary-nda"
        value={<NdaValue nda={nda} />}
      />
    </StatsBar>
  );
}

/**
 * A tile label that explains what its number means.
 *
 * <p>Every one of these six is a term of art — "furthest submission" and
 * "NDA on file" are both counts of something the reader has to already know the
 * definition of, and two of them (days-since-assignment, next follow-up) are
 * numbers somebody is expected to ACT on. A strip of six figures with no
 * definitions is a quiz.</p>
 *
 * <p>The shared {@link Tooltip}, never a native <c>title</c>: <c>title</c> never
 * opens on keyboard focus, so a keyboard user reaches the tile and is told
 * nothing. The bubble is <c>aria-hidden</c> by design, so the words are ALSO
 * rendered <c>sr-only</c> inside the label — that way the explanation is part of
 * the element's own text content and reaches assistive technology and touch
 * users, neither of whom has a hover.</p>
 */
function Hint({ text, children }: { text: string; children: ReactNode }) {
  return (
    <Tooltip label={text}>
      <span tabIndex={0} className="cursor-help focus-visible:outline-none focus-visible:shadow-goldRing rounded-sm">
        {children}
        <span className="sr-only"> — {text}</span>
      </span>
    </Tooltip>
  );
}

/**
 * 🔴 The size wrapper every badge in this strip needs, and the reason it does.
 *
 * <p><b>`StatusBadge` renders with NO font size of its own, anywhere in the
 * app.</b> Its <c>cva</c> base carries <c>text-caption</c>, but the class does
 * not survive to the DOM: <c>cn()</c> is <c>tailwind-merge</c>, and
 * tailwind-merge's default config does not know that <c>caption</c> is a font
 * size in this project's custom scale. It classifies <c>text-caption</c> as a
 * TEXT COLOUR, which puts it in the same conflict group as every tone's
 * <c>text-status-success</c> / <c>text-gray-mid</c> — and the tone string is
 * concatenated after the base, so the tone wins and the size is dropped. Verified
 * by running the real merge: <c>text-caption</c> survives for none of the eight
 * tones.</p>
 *
 * <p>Every badge therefore INHERITS its size. Almost everywhere that is
 * invisible, because the surrounding text is body-sized anyway. Inside
 * <c>StatCard</c> it is not: the value slot is <c>font-display text-h3</c>, so
 * the pill paints at h3 — a 230px badge in a 142px track, which is the clipped
 * Prequalification tile. <c>StatCard</c>'s own <c>min-w-0</c> stops the CARD
 * overflowing the grid; it cannot make a <c>whitespace-nowrap</c> child fit.</p>
 *
 * <p>The fix here is a wrapper rather than a class on the badge, precisely
 * because of the merge behaviour above: passing <c>className="text-caption"</c>
 * would win the conflict the other way and strip the tone's COLOUR instead. A
 * parent that sets the size leaves the badge's own class list untouched and the
 * badge simply inherits the right thing. The general repair belongs in the
 * shared component and is reported, not smuggled in here.</p>
 */
function Pill({ children }: { children: ReactNode }) {
  return <span className="block text-caption">{children}</span>;
}

/**
 * How this buyer's submissions stand on the NDA.
 *
 * <p>Four states, said differently on purpose. "Nothing has been presented",
 * "we are still reading", and "the read failed" are three separate facts, and
 * collapsing any of them into "none on file" would be a claim about the record
 * that the page cannot actually support.</p>
 */
export type BuyerNdaChip =
  | { status: "loading" }
  | { status: "unavailable" }
  | { status: "ready"; onFile: number; submissions: number };

/**
 * The soonest follow-up across every submission the buyer is in play on.
 *
 * <p>The buyer's OWN follow-up date no longer exists — follow-ups are
 * WorkTasks, surfaced on the list as the next action — so this roll-up reads
 * the submissions alone.</p>
 *
 * <p>Same four-state discipline as {@link BuyerNdaChip}, and for the same reason:
 * the roll-up reads a list that can still be in flight or can have failed, and
 * "no follow-up is due" is a claim a specialist will act on by not calling
 * anybody. It must never be printed on the strength of an incomplete request.</p>
 *
 * <p><c>fromSubmission</c> is carried so the card can say WHERE the winning date
 * came from. A date with no provenance sends the reader to the buyer's own
 * follow-up field to change it, which is the wrong field whenever a submission
 * won.</p>
 */
export type BuyerFollowUpChip =
  | { status: "loading" }
  | { status: "unavailable" }
  | { status: "ready"; soonest: string | null; fromSubmission: boolean };

/**
 * Resolve the roll-up. Exported so it is testable without a render, and pure so
 * it cannot acquire a query.
 *
 * <p>Unparseable dates are dropped rather than compared: <c>new Date("nonsense")
 * </c> is <c>NaN</c>, and <c>NaN</c> loses every comparison silently, so a single
 * bad row would otherwise decide the answer by accident.</p>
 */
export function resolveNextFollowUp(
  /**
   * IGNORED. The buyer's own follow-up date no longer exists; the parameter is
   * kept only so the one caller (`BuyerDetailPage`, which still passes it)
   * keeps running until it is updated to pass the submission dates alone.
   */
  _buyerDate: string | null | undefined,
  submissionDates: ReadonlyArray<string | null | undefined>,
): { soonest: string | null; fromSubmission: boolean } {
  const usable = (iso: string | null | undefined): number | null => {
    if (!iso) return null;
    const t = new Date(iso).getTime();
    return Number.isNaN(t) ? null : t;
  };

  let bestIso: string | null = null;
  let bestAt: number | null = null;
  let fromSubmission = false;

  const consider = (iso: string | null | undefined, isSubmission: boolean) => {
    const at = usable(iso);
    if (at === null) return;
    if (bestAt === null || at < bestAt) {
      bestAt = at;
      bestIso = iso as string;
      fromSubmission = isSubmission;
    }
  };

  // 🔴 The buyer's OWN follow-up date is gone — follow-ups are WorkTasks now,
  // read on the list as `nextActionDueAt`. Only the submissions contribute.
  for (const iso of submissionDates) consider(iso, true);

  return { soonest: bestIso, fromSubmission };
}

function FollowUpValue({ followUp }: { followUp: BuyerFollowUpChip }) {
  if (followUp.status === "loading") return <Quiet>Checking…</Quiet>;
  // Not toned as an error, for the same reason the NDA card is not: a failed
  // read is this page's problem, not this buyer's.
  if (followUp.status === "unavailable") return <Quiet>Unavailable</Quiet>;
  if (!followUp.soonest) return <Quiet>None set</Quiet>;

  return (
    <span className="block">
      <Timestamp value={followUp.soonest} className="text-body text-onyx" />
      <span className="block text-caption text-gray-mid">
        {followUp.fromSubmission ? "On a submission" : "On this record"}
      </span>
    </span>
  );
}

function NdaValue({ nda }: { nda: BuyerNdaChip }) {
  if (nda.status === "loading") return <Quiet>Checking…</Quiet>;
  // Not toned as an error: a failed read is this page's problem, not this
  // buyer's, and a red card would read as something wrong with the record.
  if (nda.status === "unavailable") return <Quiet>Unavailable</Quiet>;
  if (nda.submissions === 0) return <Quiet>Not presented</Quiet>;

  return (
    <span className="text-body text-onyx">
      {nda.onFile} of {nda.submissions}
    </span>
  );
}

/** A card value that is a statement about absence rather than a figure, so it
 *  does not claim the numeral's weight. */
function Quiet({ children }: { children: ReactNode }) {
  return <span className="text-body text-gray-mid">{children}</span>;
}

/**
 * Whole days elapsed since an instant; `null` when there is no usable stamp.
 *
 * Clamped at zero so a clock skew never renders a negative age, and `null`
 * rather than `0` for an unparseable value — "assigned today" and "we cannot
 * tell" must not render as the same number.
 */
function daysSince(iso: string | null | undefined, now: Date = new Date()): number | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  return Math.max(0, Math.floor((now.getTime() - then) / 86_400_000));
}
